import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Trophy, Flame, Info } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => `₦${Number(n || 0).toLocaleString()}`;
const monthLabel = (m) => {
  if (!m) return "this month";
  const [y, mm] = m.split("-").map(Number);
  return new Date(y, mm - 1, 1).toLocaleString(undefined, { month: "long" });
};

const medalTint = ["#EAB308", "#94A3B8", "#B45309"]; // gold, silver, bronze
const medalGlyph = ["🥇", "🥈", "🥉"];

/**
 * Compact "Top-earning films this month" leaderboard for the /browse hero.
 * Renders nothing if there's no data yet (opening-season silent-fail is
 * better than an "empty leaderboard" placeholder for viewers).
 */
export default function TopEarnersRail() {
  const [data, setData] = useState(null);

  useEffect(() => {
    // /public/top-films already gates on the admin toggle server-side (returns
    // films: [] when disabled). Component hides silently on empty films — no
    // extra client-side flag fetch needed.
    axios.get(`${API_URL}/public/top-films?limit=3`)
      .then((r) => setData(r.data))
      .catch((err) => {
        console.warn("[TopEarnersRail] hidden due to fetch error:", err?.response?.status || err?.message);
        setData({ films: [] });
      });
  }, []);

  if (!data || !data.films || data.films.length === 0) return null;

  return (
    <section className="mb-12" data-testid="top-earners-rail">
      <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#EAB308]/10 border border-[#EAB308]/30 flex items-center justify-center">
            <Trophy className="h-5 w-5 text-[#EAB308]" />
          </div>
          <div>
            <p className="text-eyebrow">Live leaderboard</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold">
              <span className="text-[#EAB308]">Top-earning</span> films · {monthLabel(data.month)}
            </h2>
          </div>
        </div>
        <Link
          to="/how-payouts-work"
          className="text-xs text-white/50 hover:text-[#EAB308] transition inline-flex items-center gap-1.5"
          data-testid="top-earners-link-transparency"
        >
          <Info className="h-3.5 w-3.5" /> How the math works
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="top-earners-grid">
        {data.films.map((f) => (
          <Link
            key={f.id}
            to={`/movie/${f.id}`}
            className="card-surface p-4 flex items-center gap-4 hover:border-[#EAB308]/40 transition group"
            data-testid={`top-earner-${f.rank}`}
          >
            <div
              className="text-3xl font-display font-black tabular-nums w-10 shrink-0 text-center"
              style={{ color: medalTint[f.rank - 1] || "rgba(255,255,255,0.3)" }}
              aria-label={`Rank ${f.rank}`}
            >
              {f.rank <= 3 ? medalGlyph[f.rank - 1] : `#${f.rank}`}
            </div>

            <div className="w-16 h-24 shrink-0 rounded-md overflow-hidden bg-gradient-to-br from-[#1F1F1F] to-black border border-white/5">
              {f.poster_url ? (
                <img
                  src={f.poster_url}
                  alt={f.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/20">
                  <Flame className="h-6 w-6" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p className="font-display font-bold leading-tight line-clamp-2 group-hover:text-[#EAB308] transition">
                {f.title}
              </p>
              {f.channel_name && (
                <p className="text-xs text-white/40 mt-1 truncate">{f.channel_name}</p>
              )}
              <div className="flex items-baseline gap-2 mt-2">
                <p className="font-display text-lg font-black text-[#EAB308] tabular-nums">
                  {fmt(f.earnings_ngn)}
                </p>
                <p className="text-[10px] text-white/40 uppercase tracking-wider">this month</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
